// src/components/PartialProfitTargets.jsx
import React, { useEffect } from "react";
import api from "../api/api";   // your shared axios() with baseURL "/api"

// Hard‑coded fallback if no profile is set
const FALLBACK_TARGETS = [
  { rank: 1, share: 0.30, tp_type: "RR", rr: 1.25, atr_multiplier: 1, price: "" },
  { rank: 2, share: 0.30, tp_type: "RR", rr: 2.5,  atr_multiplier: 1, price: "" },
  { rank: 3, share: 0.40, tp_type: "RR", rr: 4.0,  atr_multiplier: 1, price: "" },
];

export default function PartialProfitTargets({ accountId, targets, setTargets }) {
  const targetControlClassName = "trade-panel-target-control w-full rounded-md border border-borderDefault bg-backgroundInput text-textPrimary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary";
  const targetReadonlyClassName = "trade-panel-target-control w-full rounded-md border border-borderDefault bg-backgroundInput text-textSecondary";

  // 1. Load from API or fallback
  useEffect(() => {
    if (!accountId) {
      setTargets(FALLBACK_TARGETS);
      return;
    }

    api
      .get(`/risk/settings/${accountId}/`)
      .then(({ data }) => {
        const prof = data.default_tp_profile;
        if (prof?.partial_targets?.length) {
          // Map backend shape → UI shape
          const mapped = prof.partial_targets.map((pt, i) => ({
            rank:           i + 1,
            share:          pt.size_pct / 100,      // Convert to decimal
            tp_type:        "RR",             // defaulting to RR
            rr:             pt.r_multiple,    // RR input
            atr_multiplier: 1,                // placeholder for ATR mode
            price:          "",               // placeholder for PRICE mode
          }));
          setTargets(mapped);
        } else {
          setTargets(FALLBACK_TARGETS);
        }
      })
      .catch(() => setTargets(FALLBACK_TARGETS));
  }, [accountId, setTargets]);

  // 2. Handlers
  const handleTargetChange = (idx, field, value) => {
    setTargets((prev) =>
      prev.map((t, i) =>
        i === idx
          ? { ...t, [field]: value }
          : t
      )
    );
  };
  const addTarget = () => {
    setTargets((prev) => [
      ...prev,
      {
        rank: prev.length + 1,
        share: 0,
        tp_type: "RR",
        rr: 1.0,
        atr_multiplier: 1,
        price: "",
      },
    ]);
  };
  const removeTarget = (idx) => {
    setTargets((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((t, i) => ({ ...t, rank: i + 1 }))
    );
  };

  const getTargetGridStyle = (tpType) => (
    tpType === "PRICE"
      ? { gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 1.15fr)" }
      : { gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 0.8fr) minmax(0, 1.1fr)" }
  );

  return (
    <div className="trade-panel-muted-border mb-2 space-y-2 rounded-lg border bg-backgroundSurface/20 p-2.5">
      {targets.map((t, idx) => (
        <div key={idx} className="trade-panel-muted-border rounded-md border bg-backgroundSurface/30 p-2.5">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="trade-panel-target-chip font-medium text-textSecondary">Target {idx + 1}</span>
            <button
              onClick={() => removeTarget(idx)}
              className="trade-panel-target-remove font-medium text-destructive transition hover:text-destructiveHover"
            >
              Remove
            </button>
          </div>

          <div className="grid gap-2" style={getTargetGridStyle(t.tp_type)}>
            <div className="min-w-0">
              <label className="trade-panel-target-label mb-1 block font-medium text-textSecondary">Share %</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={t.share * 100}
                onChange={(e) =>
                  handleTargetChange(idx, "share", (parseFloat(e.target.value) || 0) / 100)
                }
                className={targetControlClassName}
              />
            </div>

            <div className="min-w-0">
              <label className="trade-panel-target-label mb-1 block font-medium text-textSecondary">Type</label>
              <select
                value={t.tp_type}
                onChange={(e) =>
                  handleTargetChange(idx, "tp_type", e.target.value)
                }
                className={targetControlClassName}
              >
                <option value="RR">RR</option>
                <option value="ATR">ATR</option>
                <option value="PRICE">PRICE</option>
              </select>
            </div>

            {t.tp_type === "RR" && (
              <div className="min-w-0">
                <label className="trade-panel-target-label mb-1 block font-medium text-textSecondary">R/R</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={t.rr}
                  onChange={(e) =>
                    handleTargetChange(idx, "rr", parseFloat(e.target.value) || 0)
                  }
                  className={targetControlClassName}
                />
              </div>
            )}

            {t.tp_type === "ATR" && (
              <div className="min-w-0">
                <label className="trade-panel-target-label mb-1 block font-medium text-textSecondary">ATR x</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={t.atr_multiplier}
                  onChange={(e) =>
                    handleTargetChange(idx, "atr_multiplier", parseFloat(e.target.value) || 0)
                  }
                  className={targetControlClassName}
                />
              </div>
            )}

            <div className="min-w-0">
              <label className="trade-panel-target-label mb-1 block font-medium text-textSecondary">
                {t.tp_type === "PRICE" ? "Price" : "Target"}
              </label>
              {t.tp_type === "PRICE" ? (
                <input
                  type="number"
                  step="0.00001"
                  value={t.price}
                  onChange={(e) =>
                    handleTargetChange(
                      idx,
                      "price",
                      e.target.value === "" ? "" : parseFloat(e.target.value) || 0
                    )
                  }
                  className={targetControlClassName}
                />
              ) : (
                <input
                  type="text"
                  value={t.price === "" ? "Auto" : t.price}
                  readOnly
                  className={targetReadonlyClassName}
                />
              )}
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={addTarget}
        className="trade-panel-muted-border trade-panel-target-add inline-flex items-center rounded-md border bg-transparent font-medium text-textPrimary transition hover:bg-secondary/10"
      >
        + Add Target
      </button>
    </div>
  );
}
